'use client';

import React, { memo, useCallback, useContext, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import {
  Delete,
  NextKeyframeFill,
  PreviousKeyframeFill,
} from '@crabs-ai/web-icons';
import { Button, ToolButton, ValueInputField } from '@crabs-ai/ds-react';
import { PlayerRefContext } from '../../context-provider';
import {
  clampZoomRect,
  findNextZoomKeyframe,
  findPrevZoomKeyframe,
  getZoomRectAt,
  hasZoomKeyframeAt,
  MIN_ZOOM_SIZE,
  removeZoomKeyframeAt,
  upsertZoomKeyframe,
  type ZoomKeyframe,
  type ZoomRect,
} from '../../items/animation/zoom-keyframes';
import type { CanHaveZoomKeyframes } from '../../items/shared';
import { changeItem } from '../../state/actions/change-item';
import {
  selectItemForZoomRegion,
  unselectItemForZoomRegion,
} from '../../state/actions/zoom-region';
import { EditorStarterItem } from '../../items/item-type';
import {
  useFullState,
  useItem,
  useWriteContext,
} from '../../utils/use-context';
import { useCurrentPlayerFrame } from '../../utils/use-current-player-frame';

const MAX_ZOOM_SCALE = 1 / MIN_ZOOM_SIZE;

/**
 * 줌 키프레임 다이아 마커 — position-keyframe-controls 와 같은 에디터 그래픽.
 * 색은 currentColor 이므로 text-* 로 제어한다.
 */
const KeyframeDiamond: React.FC<{ filled: boolean; className?: string }> = ({
  filled,
  className,
}) => (
  <svg
    viewBox="0 0 16 16"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M8 1.5 14.5 8 8 14.5 1.5 8Z"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * 헤더 컨트롤 행 — [‹ 이전] [◆/◇ 토글] [› 다음] … [🗑 전체삭제].
 * 플레이어 프레임 구독은 이 소형 컴포넌트에만 건다(재생 중 상위 리렌더 방지).
 */
const ZoomKeyframeHeaderControls: React.FC<{
  keyframes: ZoomKeyframe[];
  from: number;
  maxLocalFrame: number;
  onSeekToLocalFrame: (localFrame: number) => void;
  onAddAt: (localFrame: number) => void;
  onRemoveAt: (localFrame: number) => void;
  onRemoveAll: () => void;
}> = ({
  keyframes,
  from,
  maxLocalFrame,
  onSeekToLocalFrame,
  onAddAt,
  onRemoveAt,
  onRemoveAll,
}) => {
  const t = useTranslations();
  const globalFrame = useCurrentPlayerFrame();
  const localFrame = Math.round(globalFrame - from);
  const isInRange = localFrame >= 0 && localFrame <= maxLocalFrame;
  const hasKf = isInRange && hasZoomKeyframeAt(keyframes, localFrame);
  const prevKf = findPrevZoomKeyframe(keyframes, localFrame);
  const nextKf = findNextZoomKeyframe(keyframes, localFrame);

  return (
    <div className="flex items-center gap-4xs">
      <ToolButton
        size="sm"
        state={prevKf ? 'default' : 'disabled'}
        onClick={() => {
          if (prevKf) onSeekToLocalFrame(prevKf.frame);
        }}
        aria-label={t('Editor.inspector.zoom-keyframes.prev')}
        isShowTooltip
        tooltipNode={t('Editor.inspector.zoom-keyframes.prev')}
      >
        <PreviousKeyframeFill />
      </ToolButton>

      <ToolButton
        size="sm"
        state={isInRange ? 'default' : 'disabled'}
        onClick={() => {
          if (!isInRange) return;
          if (hasKf) onRemoveAt(localFrame);
          else onAddAt(localFrame);
        }}
        aria-label={
          hasKf
            ? t('Editor.inspector.zoom-keyframes.toggle-remove')
            : t('Editor.inspector.zoom-keyframes.toggle-add')
        }
        isShowTooltip
        tooltipNode={
          hasKf
            ? t('Editor.inspector.zoom-keyframes.toggle-remove')
            : t('Editor.inspector.zoom-keyframes.toggle-add')
        }
      >
        <KeyframeDiamond
          filled={hasKf}
          className={
            !isInRange
              ? undefined
              : hasKf
                ? 'text-sys-cont-primary-default'
                : 'text-sys-cont-neutral-light'
          }
        />
      </ToolButton>

      <ToolButton
        size="sm"
        state={nextKf ? 'default' : 'disabled'}
        onClick={() => {
          if (nextKf) onSeekToLocalFrame(nextKf.frame);
        }}
        aria-label={t('Editor.inspector.zoom-keyframes.next')}
        isShowTooltip
        tooltipNode={t('Editor.inspector.zoom-keyframes.next')}
      >
        <NextKeyframeFill />
      </ToolButton>

      {keyframes.length > 0 && (
        <div className="flex min-w-0 flex-1 justify-end">
          <ToolButton
            size="sm"
            onClick={onRemoveAll}
            aria-label={t('Editor.inspector.zoom-keyframes.remove-all')}
            isShowTooltip
            tooltipNode={t('Editor.inspector.zoom-keyframes.remove-all')}
          >
            <Delete />
          </ToolButton>
        </div>
      )}
    </div>
  );
};

/**
 * 현재 플레이헤드 시점의 줌 편집 — CapCut 식. 배율(x)·중심(%) 을 바꾸면 그 시점에
 * 키프레임이 자동 생성/갱신된다(auto-keyframe). 키프레임 목록 대신 이 한 행만 노출.
 * 저장은 left/top/size fraction 으로 환산한다. 플레이헤드 구독은 이 소형 컴포넌트에만.
 */
const ZoomCurrentValueEditorUnmemoized: React.FC<{
  keyframes: ZoomKeyframe[];
  from: number;
  maxLocalFrame: number;
  onUpsertAt: (
    localFrame: number,
    rect: ZoomRect,
    commitToUndoStack: boolean,
  ) => void;
}> = ({ keyframes, from, maxLocalFrame, onUpsertAt }) => {
  const t = useTranslations();
  const globalFrame = useCurrentPlayerFrame();
  const localFrame = Math.round(globalFrame - from);
  const isInRange = localFrame >= 0 && localFrame <= maxLocalFrame;
  const rect = getZoomRectAt(keyframes, localFrame);
  const hasKf = isInRange && hasZoomKeyframeAt(keyframes, localFrame);

  const scaleValue = 1 / rect.size;
  const centerX = rect.left + rect.size / 2;
  const centerY = rect.top + rect.size / 2;

  // 배율·중심 편집 — 중심 고정으로 left/top 재계산 후 클램프해 upsert
  const applyRect = (
    nextScale: number,
    nextCx: number,
    nextCy: number,
    commitToUndoStack: boolean,
  ) => {
    if (!isInRange) return;
    const size = 1 / Math.min(MAX_ZOOM_SCALE, Math.max(1, nextScale));
    onUpsertAt(
      localFrame,
      clampZoomRect({
        size,
        left: nextCx - size / 2,
        top: nextCy - size / 2,
      }),
      commitToUndoStack,
    );
  };

  return (
    <div className="flex items-center gap-4xs">
      <KeyframeDiamond
        filled={hasKf}
        className={`size-2 shrink-0 ${
          hasKf
            ? 'text-sys-cont-primary-default'
            : 'text-sys-cont-neutral-light'
        }`}
      />
      <ValueInputField
        value={Number(scaleValue.toFixed(1))}
        setValue={(update) =>
          applyRect(update.num, centerX, centerY, update.commitToUndoStack)
        }
        min={1}
        max={MAX_ZOOM_SCALE}
        step={0.1}
        isUnit="x"
        showStepper={false}
        disabled={!isInRange}
        accessibilityLabel={t('Editor.inspector.zoom-keyframes.scale')}
        className="w-[64px] shrink-0"
      />
      <ValueInputField
        value={Math.round(centerX * 100)}
        setValue={(update) =>
          applyRect(
            scaleValue,
            update.num / 100,
            centerY,
            update.commitToUndoStack,
          )
        }
        min={0}
        max={100}
        step={1}
        isUnit="%"
        showStepper={false}
        disabled={!isInRange}
        accessibilityLabel={t('Editor.inspector.zoom-keyframes.center-x')}
        className="w-[64px] shrink-0"
      />
      <ValueInputField
        value={Math.round(centerY * 100)}
        setValue={(update) =>
          applyRect(
            scaleValue,
            centerX,
            update.num / 100,
            update.commitToUndoStack,
          )
        }
        min={0}
        max={100}
        step={1}
        isUnit="%"
        showStepper={false}
        disabled={!isInRange}
        accessibilityLabel={t('Editor.inspector.zoom-keyframes.center-y')}
        className="w-[64px] shrink-0"
      />
    </div>
  );
};

const ZoomCurrentValueEditor = memo(ZoomCurrentValueEditorUnmemoized);

/**
 * 줌 키프레임 편집 — "이 시점에 이 영역을 확대" 를 기록하는 CapCut 식 punch-in.
 *
 * - ◆/◇ 토글: 플레이헤드에 키프레임이 있으면 삭제, 없으면 그 시점 보간 rect 그대로
 *   추가(추가 자체는 화면을 안 바꾼다).
 * - 영역 편집: 캔버스에 bbox 오버레이(ZoomRegionOverlay)를 띄워 드래그로 영역 지정.
 * - 저장은 아이템의 zoomKeyframes 필드(1급 데이터). v1 은 단일 선택 + croppable
 *   타입 전용 — 호출부(AnimationControls)가 게이트한다.
 */
const ZoomKeyframeControlsUnmemoized: React.FC<{
  itemId: string;
  durationInFrames: number;
}> = ({ itemId, durationInFrames }) => {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const playerRef = useContext(PlayerRefContext);
  const item = useItem(itemId);
  const state = useFullState();
  const isEditingRegion = state.itemSelectedForZoomRegion === itemId;

  const keyframes = useMemo<ZoomKeyframe[]>(
    () =>
      item && 'zoomKeyframes' in item && Array.isArray(item.zoomKeyframes)
        ? item.zoomKeyframes
        : [],
    [item],
  );

  const from = item?.from ?? 0;

  const commit = useCallback(
    (next: ZoomKeyframe[], commitToUndoStack = true) => {
      setState({
        update: (s) =>
          changeItem(s, itemId, (i) => {
            const prev = i as EditorStarterItem & CanHaveZoomKeyframes;
            if (next.length === 0) {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { zoomKeyframes: _removed, ...rest } = prev;
              return rest as EditorStarterItem;
            }
            return { ...prev, zoomKeyframes: next };
          }),
        commitToUndoStack,
      });
    },
    [setState, itemId],
  );

  const seekToLocalFrame = useCallback(
    (localFrame: number) => {
      playerRef?.current?.seekTo(Math.round(from + localFrame));
    },
    [playerRef, from],
  );

  // 추가 — 그 시점 곡선 값을 그대로 기록(추가 자체는 화면을 안 바꾼다).
  const addAt = useCallback(
    (localFrame: number) => {
      const rect = getZoomRectAt(keyframes, localFrame);
      commit(upsertZoomKeyframe(keyframes, { frame: localFrame, ...rect }));
    },
    [keyframes, commit],
  );

  const removeAt = useCallback(
    (localFrame: number) => {
      commit(removeZoomKeyframeAt(keyframes, localFrame));
    },
    [keyframes, commit],
  );

  const removeAll = useCallback(() => {
    commit([]);
    setState({
      update: unselectItemForZoomRegion,
      commitToUndoStack: false,
    });
  }, [commit, setState]);

  // CapCut 식 auto-keyframe — 현재 시점 값 편집이 곧 그 시점 키프레임 upsert.
  const upsertAt = useCallback(
    (localFrame: number, rect: ZoomRect, commitToUndoStack: boolean) => {
      commit(
        upsertZoomKeyframe(keyframes, { frame: localFrame, ...rect }),
        commitToUndoStack,
      );
    },
    [keyframes, commit],
  );

  // 오버레이 드래그는 "플레이헤드 시점"에 키프레임을 upsert 한다 — 새 시점에서
  // 드래그하면 새 키프레임, 기존 키프레임 시점(행 클릭으로 시크)에서 드래그하면
  // 그 키프레임 수정. 진입 시 플레이헤드를 옮기지 않는다(새 키프레임 추가 흐름 보존).
  const toggleRegionEditing = useCallback(() => {
    setState({
      update: (s) =>
        s.itemSelectedForZoomRegion === itemId
          ? unselectItemForZoomRegion(s)
          : selectItemForZoomRegion({ state: s, itemId }),
      commitToUndoStack: false,
    });
  }, [setState, itemId]);

  const maxLocalFrame = Math.max(0, durationInFrames - 1);

  // 섹션 제목은 호출부(AnimationControls)의 ControlsHeader 가 담당한다.
  return (
    <div className="flex flex-col gap-2xs">
      <ZoomKeyframeHeaderControls
        keyframes={keyframes}
        from={from}
        maxLocalFrame={maxLocalFrame}
        onSeekToLocalFrame={seekToLocalFrame}
        onAddAt={addAt}
        onRemoveAt={removeAt}
        onRemoveAll={removeAll}
      />

      <ZoomCurrentValueEditor
        keyframes={keyframes}
        from={from}
        maxLocalFrame={maxLocalFrame}
        onUpsertAt={upsertAt}
      />

      <Button
        size="xsmall"
        hierarchy={isEditingRegion ? 'primary-variant' : 'tertiary'}
        className="w-full"
        onClick={toggleRegionEditing}
      >
        {isEditingRegion
          ? t('Editor.inspector.zoom-keyframes.edit-region-done')
          : t('Editor.inspector.zoom-keyframes.edit-region')}
      </Button>

      {keyframes.length === 0 && (
        <span className="text-caption-small text-sys-cont-neutral-lightest">
          {t('Editor.inspector.zoom-keyframes.empty')}
        </span>
      )}
    </div>
  );
};

export const ZoomKeyframeControls = memo(ZoomKeyframeControlsUnmemoized);
